import { Flex, Grid, Heading, Text, View } from "@aws-amplify/ui-react";
import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProductAndPricesQuery } from "../util/query/getProductAndPricesQuery";
import { getCheckoutSession } from "../services/stripe/getCheckoutSession";
import { useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard.new";
import useEnvironment from "../hooks/useEnvironment";
import { useUserContext } from "../context";
import { toast } from "react-toastify";
import { useFreeTrials } from "../features/premium/hooks/useFreeTrials";
import { startFreeTrial } from "../features/premium";
import { useFreeTrialAvailable } from "../features/premium/hooks";
import RestAPI from "../util/RestAPI";
import useModal from "../hooks/useModal";
import { useCanvasConffeti } from "../hooks/useCanvasConfetti";

export type PremiumPricingCardProps = {
  interval: "month" | "year";
  discount?: number;
  promoId?: string;
};

const testPremiumProductId = "prod_PEe8ynbzgTjE7D";
const livePremiumProductId = "prod_PIomGnbtITLrzy";

export default function PremiumPricingCard(props: PremiumPricingCardProps) {
  const { interval, discount, promoId } = props;
  const { fireCannon } = useCanvasConffeti();
  const { user, authenticated } = useUserContext();

  const { environment } = useEnvironment();
  const queryClient = useQueryClient();

  const productId = React.useMemo(
    () =>
      environment === "prod" ? livePremiumProductId : testPremiumProductId,
    [environment]
  );

  const navigate = useNavigate();

  const { data, isInitialLoading: isLoadingProduct } = useQuery(
    getProductAndPricesQuery(productId)
  );

  const { prices } = React.useMemo(
    () =>
      data !== undefined ? data : { product: undefined, prices: undefined },
    [data]
  );

  const price = React.useMemo(() => {
    return prices?.data.find((price) => price.recurring.interval === interval);
  }, [prices, interval]);

  const pricingMap = {
    month: {
      usd: 8.99,
      cad: 11.99,
    },
    year: {
      usd: 89.99,
      cad: 117.99,
    },
  };

  // const cadPrice = React.useMemo(() => {
  //   return price?.currency_options?.cad.unit_amount;
  // }, [price]);

  // const usdPrice = React.useMemo(() => {
  //   return price?.currency_options?.usd.unit_amount;
  // }, [price]);

  // const priceValueString = React.useMemo(() => {
  //   return price
  //     ? "$" + price.unit_amount / 100 + " " + price.currency.toUpperCase()
  //     : undefined;
  // }, [price]);

  // const { data: checkoutSession } = useQuery(
  //   ["stripe", "checkoutSession", price?.id],
  //   () => getCheckoutSession(price?.id ? price.id : ""),
  //   { enabled: price !== undefined }
  // );

  const [checkoutLoading, setCheckoutLoading] = React.useState<boolean>(false);

  const { data: freeTrials, isInitialLoading: loadingTrials } = useFreeTrials();
  const { data: trialInfo, isInitialLoading: loadingAvailability } =
    useFreeTrialAvailable("7-day");

  const { data: isSubscribed, isInitialLoading: loadingSubscribed } = useQuery({
    queryKey: ["users", "subscriptions", "active"],
    queryFn: async () => {
      const resp = await RestAPI.get("/users/subscriptions/active");
      return (await resp.json()) as any as Boolean;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: authenticated,
  });

  const activeTrial = freeTrials?.find(
    (trial) => trial.trialStatus === "active"
  );

  const showCheckout = React.useCallback(async () => {
    if (user) {
      setCheckoutLoading(true);
      try {
        const checkoutSession = await getCheckoutSession(
          price?.id ? price.id : "",
          promoId
        );
        window.location.href = checkoutSession.url;
      } catch (err) {
        console.error(err);
        toast.error("Error starting checkout session");
        setCheckoutLoading(false);
      }
    } else {
      navigate("/auth/signup");
    }
    setCheckoutLoading(false);
  }, [user, price, promoId]);

  const { mutateAsync: startTrial_mutateAsync, isLoading: startingTrial } =
    useMutation({
      mutationKey: ["start-trial"],
      mutationFn: async () => {
        await startFreeTrial("7-day");
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["user"]);
        queryClient.invalidateQueries(["free-trial"]);
        setShowFreeTrialModal(true);
      },
      onError: () => {
        toast.error("Something went wrong. Are you already on Premium?");
      },
    });

  const buttonType:
    | "starter"
    | "current-plan"
    | "trial-ready"
    | "trial-active" = React.useMemo(() => {
    if (trialInfo?.available) {
      return "trial-ready";
    }
    if (activeTrial && !isSubscribed) {
      return "trial-active";
    }
    if (user?.customerTier === "Premium" || isSubscribed) {
      return "current-plan";
    }
    return "starter";
  }, [trialInfo, activeTrial, user?.customerTier, isSubscribed]);

  const buttonText = React.useMemo(() => {
    switch (buttonType) {
      case "starter":
        return "Get Started";
      case "current-plan":
        return "Current Plan";
      case "trial-ready":
        return "7-Day Free Trial";
      case "trial-active":
        return "Free Trial Active";
    }
  }, [buttonType]);

  const [FreeTrialModal, setShowFreeTrialModal] = useModal(
    {
      title: "You're a rockstar 🥳",

      ReactComponent: () => (
        <Flex
          width={"100%"}
          height={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Heading>Enjoy your 7-day free trial!</Heading>
        </Flex>
      ),

      onShow: () => {
        fireCannon("left", false);
        fireCannon("right", false);
      },
    },
    []
  );

  return (
    <>
      <FreeTrialModal />
      <ProductCard
        notification={trialInfo?.available}
        title={"Premium"}
        isLoading={
          isLoadingProduct ||
          checkoutLoading ||
          startingTrial ||
          loadingTrials ||
          loadingAvailability ||
          loadingSubscribed
        }
        heading={
          <View paddingTop={"small"}>
            <Grid templateColumns={"1fr auto 1fr"} gap={"xs"}>
              {discount && (
                <Text
                  textAlign={"right"}
                  color={"teal.60"}
                  alignSelf={"center"}
                  fontFamily={"Poppins"}
                  fontSize={"small"}
                  textDecoration={"line-through"}
                >
                  ${pricingMap[interval].usd}
                </Text>
              )}
              <Text
                column={2}
                fontWeight={"bold"}
                fontFamily={"Poppins"}
                fontSize={"large"}
              >
                $
                {(
                  pricingMap[interval].usd * (discount ? 1 - discount : 1)
                ).toFixed(2)}{" "}
                USD
              </Text>
            </Grid>
            <Grid templateColumns={"1fr auto 1fr"} gap={"xs"}>
              {discount && (
                <Text
                  textAlign={"right"}
                  color={"teal.60"}
                  alignSelf={"center"}
                  fontFamily={"Poppins"}
                  fontSize={"small"}
                  textDecoration={"line-through"}
                >
                  ${pricingMap[interval].cad.toFixed(2)}
                </Text>
              )}
              <Text
                column={2}
                fontWeight={"semibold"}
                fontFamily={"Poppins"}
                fontSize={"larger"}
              >
                $
                {(
                  pricingMap[interval].cad * (discount ? 1 - discount : 1)
                ).toFixed(2)}{" "}
                CAD
              </Text>
            </Grid>
          </View>
        }
        subheading={
          <Text
            fontWeight={"semibold"}
            height={"60px"}
            fontSize={"small"}
            padding={"xs"}
            paddingBottom={"medium"}
            color={"teal.100"}
          >
            charged {interval}ly
            {interval === "year" ? (
              <Text
                as="span"
                fontWeight={"semibold"}
                color={"red.60"}
                fontFamily={"Playpen Sans"}
                fontSize={"small"}
              >
                {" "}
                (Save 18%)
              </Text>
            ) : null}
          </Text>
        }
        buttonText={buttonText}
        highlightColor={"teal.60"}
        onClick={async () => {
          if (!authenticated) {
            navigate("/auth/signup");
            return;
          }

          switch (buttonType) {
            case "trial-ready":
              if (!trialInfo) {
                toast.error("Something went wrong");
                return;
              }
              if (trialInfo.available) {
                await startTrial_mutateAsync();
              } else {
                toast.info(trialInfo.message);
              }
              break;
            case "current-plan":
              toast.info("You are already on the Premium plan 🎉");
              break;
            default:
              showCheckout();
              break;
          }
        }}
        features={[
          "Everything in Starter",
          "Unlimited AI text generation tokens/month",
          "Unlimited credits in our Canva app",
          "Curriculum alignment in Canva app",
          "Suggested images in Canva app",
          "Smarter AI Model",
          "Translation capabilities",
          "Future LMS integrations",
          "Priority support",
          "Feature request priority",
          "Early access to major feature updates",
        ]}
      />
    </>
  );
}
